import { stopPollingMessages } from './stopPollingMessages';
import {
  setChatbotHasPollAnswers,
  setChatbotRowVersion,
  setChatbotUnreadMessagesCount,
  setChatbotPeriodicity,
  setChatbotPollingIntervalId,
  setChatbotIsTyping,
} from '../../actions/chatbot/actions';
import { setMessagesFulfilled } from '../../actions/messages';
import { setSendMessageAbort } from '../../actions/sendMessage';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { fetchChatbotMessages } from '../../services/fetchChatbotMessages';
import { fetchChatbotReadMessages } from '../../services/fetchChatbotReadMessages';
import { TMessageSchema } from '../../types/messages';
import { TThunkAction } from '../../types/redux/thunk';
import { findLastMessage } from '../../utils/findLastMessage';

const STATUS_CHANGE_TIMEOUT = 1000;
const WAIT_MESSAGE_TIMEOUT = 60000;

type TStartPollingMessagesParams = {
  messageIdToDelete?: string;
  isQuick?: boolean;
} | void;

export const startPollingMessages =
  ({ messageIdToDelete, isQuick = false }: TStartPollingMessagesParams = {}): TThunkAction<void> =>
  async (dispatch, getState, context) => {
    dispatch(stopPollingMessages());

    let waitMessageTimeout: number;
    const pollingPeriod = context.config.get<number>('frontend-chats.chatbotPollingPeriod') || 5000;
    const quickpollingPeriod = context.config.get<number>('frontend-chats.chatbotQuickPollingPeriod') || 5000;

    const pollingFunction = async () => {
      const {
        chatbot: { rowVersion },
        chatWindow: { currentOpenedChatId },
      } = getState();

      const response = await fetchChatbotMessages(context, rowVersion);

      /* istanbul ignore next */
      if (!response) {
        return;
      }

      if (currentOpenedChatId === CHATBOT_ID && response.unreadMessagesCount) {
        // тут осознанно нет await, так как клиент выставляет цифру
        fetchChatbotReadMessages(context);
      }

      if (messageIdToDelete) {
        dispatch(
          setSendMessageAbort({
            message: { chatId: CHATBOT_ID } as TMessageSchema,
            messageId: messageIdToDelete,
            messagesMap: context.custom.messagesMap,
          }),
        );
      }

      const isChatbotTyping = !(response.messages[0]?.fromChatBot ?? !isQuick);

      dispatch(setChatbotIsTyping(isChatbotTyping));
      dispatch(
        setMessagesFulfilled({
          messages: response.messages,
          chatId: CHATBOT_ID,
          messagesMap: context.custom.messagesMap,
        }),
      );

      const { openedChats } = getState();

      const chatbotChat = openedChats[CHATBOT_ID];

      const { pages } = chatbotChat;

      const pagesLastIndex = pages.length - 1;
      const { messages } = pages[pagesLastIndex];
      const messagesLastIndex = messages.length - 1;

      const lastMessage = findLastMessage({
        chat: chatbotChat,
        pageIndex: pagesLastIndex,
        messageIndex: messagesLastIndex,
        incoming: false,
      });

      dispatch(setChatbotRowVersion(lastMessage?.rowVersion || 0));
      dispatch(setChatbotUnreadMessagesCount(currentOpenedChatId === CHATBOT_ID ? 0 : response.unreadMessagesCount));
      dispatch(setChatbotPeriodicity(response.periodicity));
      dispatch(setChatbotHasPollAnswers(response.hasPollAnswers));

      if (lastMessage?.fromChatBot && isQuick) {
        clearTimeout(waitMessageTimeout);
        dispatch(startPollingMessages());
      }
    };

    const pollingPromise = pollingFunction();
    let isPollingPending = true;

    setTimeout(() => {
      if (isPollingPending) {
        dispatch(setChatbotIsTyping(true));
      }
    }, STATUS_CHANGE_TIMEOUT);

    await pollingPromise.finally(() => {
      isPollingPending = false;
    });

    const intervalId = window.setInterval(pollingFunction, isQuick ? quickpollingPeriod : pollingPeriod);

    if (isQuick) {
      waitMessageTimeout = window.setTimeout(() => {
        dispatch(startPollingMessages());
      }, WAIT_MESSAGE_TIMEOUT);
    }

    dispatch(setChatbotPollingIntervalId(intervalId));
  };
